import { useLangue } from '../../composants/useLangue.js';
import { Button, Checkbox, Input, Select } from '@artosera/ui';
import { formatPrice, REGIMES_TVA, type RegimeTva } from '@artosera/shared';
import { Download } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { GalerieSession } from '../../api/types.js';
import { estApiError } from '../../api/errors.js';
import {
  useModifierProfilFiscal,
  useProfilFiscal,
  ventesApi,
  type CorpsMiseAJourProfil,
  type ProfilFiscal,
} from '../../api/ventes.js';
import { galerieALaction } from '../../auth/sujet.js';
import { Field } from '../Field.js';
import { enregistrerFichier } from '../../api/client.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import styles from './ReglagesFacturationTab.module.css';

export interface ReglagesFacturationTabProps {
  galerieSlug: string;
  galerie: GalerieSession;
}

/**
 * Réglages de facturation (3.3, écran 5 ; § 17.2, § 17.10) : profil fiscal
 * (émetteur, TVA, droit de suite, mentions, délai) et export comptable. Le
 * modèle de facture a rejoint Réglages › Documents (D21) : les modèles sont un
 * réglage du cœur, ouvert même sans le module `ventes` ; un renvoi y mène.
 */
export function ReglagesFacturationTab({
  galerieSlug,
  galerie,
}: ReglagesFacturationTabProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const profil = useProfilFiscal(galerieSlug);
  const peutExporter = galerieALaction(galerie, 'invoices:export');
  const peutReglerModeles = galerieALaction(galerie, 'settings:templates');

  if (profil.isLoading) {
    return <p className={styles.vide}>{t('commun:chargement')}</p>;
  }
  if (profil.isError || !profil.data) {
    return (
      <p className={styles.vide} role="alert">
        {estApiError(profil.error) ? profil.error.message : t('commun:erreurReseau')}
      </p>
    );
  }

  return (
    <div className={styles.page}>
      <ProfilFiscalForm
        key={`profil-${profil.data.profilFiscal.modifieLe}`}
        galerieSlug={galerieSlug}
        profil={profil.data.profilFiscal}
      />
      <div className={styles.annexes}>
        {peutReglerModeles ? (
          <section className={styles.section}>
            <h2 className={styles.sectionTitre}>{t('regie:ventes.reglages.modeleTitre')}</h2>
            <p className={styles.aide}>
              {t('regie:ventes.reglages.modeleDeplace')}{' '}
              <Link to={`/${galerieSlug}/reglages/documents`}>
                {t('regie:ventes.reglages.modeleLien')}
              </Link>
            </p>
          </section>
        ) : null}
        {peutExporter ? <ExportComptableSection galerieSlug={galerieSlug} /> : null}
      </div>
    </div>
  );
}

function ExportComptableSection({ galerieSlug }: { galerieSlug: string }): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const anneeCourante = new Date().getFullYear();
  const [annee, setAnnee] = useState(String(anneeCourante));
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function telecharger(): Promise<void> {
    setErreur(null);
    setEnCours(true);
    try {
      const fichier = await dansLaPorteeCourante((appel) =>
        ventesApi.exporter(
          galerieSlug,
          { ...(annee.trim() ? { annee: Number(annee) } : {}), format },
          appel,
        ),
      );
      enregistrerFichier(fichier, `export-ventes.${format}`);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitre}>{t('regie:ventes.reglages.exportTitre')}</h2>
      <p className={styles.aide}>{t('regie:ventes.reglages.exportAide')}</p>

      <div className={styles.grille2}>
        <Field label={t('regie:ventes.reglages.exportAnnee')} htmlFor="export-annee">
          <Input
            id="export-annee"
            type="number"
            value={annee}
            onChange={(event) => setAnnee(event.target.value)}
          />
        </Field>
        <Field label={t('regie:ventes.reglages.exportFormat')} htmlFor="export-format">
          <Select
            id="export-format"
            aria-label={t('regie:ventes.reglages.exportFormat')}
            value={format}
            onValueChange={(valeur) => setFormat(valeur as 'csv' | 'json')}
            options={[
              { value: 'csv', label: t('regie:ventes.reglages.exportCsv') },
              { value: 'json', label: t('regie:ventes.reglages.exportJson') },
            ]}
          />
        </Field>
      </div>

      {erreur ? (
        <p className={styles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}

      <div className={styles.actions}>
        <Button variant="secondary" onClick={() => void telecharger()} loading={enCours}>
          <Download size={16} strokeWidth={1.5} aria-hidden />
          {t('regie:ventes.reglages.exportBouton')}
        </Button>
      </div>
    </section>
  );
}

function ProfilFiscalForm({
  galerieSlug,
  profil,
}: {
  galerieSlug: string;
  profil: ProfilFiscal;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'ventes', 'commun']);
  const langue = useLangue();
  const modifier = useModifierProfilFiscal(galerieSlug);

  const [assujetti, setAssujetti] = useState(profil.assujetti);
  const [franchiseEnBase, setFranchiseEnBase] = useState(profil.franchiseEnBase);
  const [numeroTva, setNumeroTva] = useState(profil.numeroTva ?? '');
  const [regimeParDefaut, setRegimeParDefaut] = useState<RegimeTva>(profil.regimeParDefaut);
  const [delaiPaiementJours, setDelaiPaiementJours] = useState(String(profil.delaiPaiementJours));
  const [droitDeSuiteAChargeAcheteur, setDroitDeSuiteAChargeAcheteur] = useState(
    profil.droitDeSuiteAChargeAcheteur,
  );
  const [droitDeSuiteBeneficiaire, setDroitDeSuiteBeneficiaire] = useState(
    profil.droitDeSuiteBeneficiaire ?? '',
  );
  const [raisonSociale, setRaisonSociale] = useState(profil.emetteur.raisonSociale);
  const [siren, setSiren] = useState(profil.emetteur.siren ?? '');
  const [tvaIntracom, setTvaIntracom] = useState(profil.emetteur.tvaIntracom ?? '');
  const [ligne1, setLigne1] = useState(profil.emetteur.adresse.ligne1);
  const [codePostal, setCodePostal] = useState(profil.emetteur.adresse.codePostal);
  const [ville, setVille] = useState(profil.emetteur.adresse.ville);
  const [pays, setPays] = useState(profil.emetteur.adresse.pays || 'FR');
  const [iban, setIban] = useState(profil.emetteur.iban ?? '');
  const [bic, setBic] = useState(profil.emetteur.bic ?? '');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState(false);

  async function soumettre(): Promise<void> {
    setErreur(null);
    setEnregistre(false);
    const corps: CorpsMiseAJourProfil = {
      assujetti,
      franchiseEnBase,
      regimeParDefaut,
      delaiPaiementJours: Number(delaiPaiementJours) || 0,
      droitDeSuiteAChargeAcheteur,
      ...(numeroTva.trim() ? { numeroTva: numeroTva.trim() } : {}),
      ...(droitDeSuiteBeneficiaire.trim()
        ? { droitDeSuiteBeneficiaire: droitDeSuiteBeneficiaire.trim() }
        : {}),
      emetteur: {
        raisonSociale,
        ...(siren.trim() ? { siren: siren.trim() } : {}),
        ...(tvaIntracom.trim() ? { tvaIntracom: tvaIntracom.trim() } : {}),
        adresse: { ligne1, codePostal, ville, pays },
        ...(iban.trim() ? { iban: iban.trim() } : {}),
        ...(bic.trim() ? { bic: bic.trim() } : {}),
        mentionsComplementaires: [...profil.emetteur.mentionsComplementaires],
        escompte: profil.emetteur.escompte,
      },
    };
    try {
      await modifier.mutateAsync(corps);
      setEnregistre(true);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  const villeComplete = [codePostal, ville].filter((partie) => partie.trim()).join(' ');

  return (
    <section className={styles.profil}>
      <div className={styles.profilFormulaire}>
        <h2 className={styles.sectionTitre}>{t('regie:ventes.reglages.profilTitre')}</h2>
        <p className={styles.aide}>{t('regie:ventes.reglages.profilAide')}</p>

        <div className={styles.groupe}>
          <div className={styles.grille2}>
            <Field label={t('regie:ventes.reglages.raisonSociale')} htmlFor="pf-raison-sociale">
              <Input
                id="pf-raison-sociale"
                value={raisonSociale}
                onChange={(event) => setRaisonSociale(event.target.value)}
              />
            </Field>
            <Field label={t('regie:ventes.reglages.siren')} htmlFor="pf-siren">
              <Input
                id="pf-siren"
                value={siren}
                onChange={(event) => setSiren(event.target.value)}
              />
            </Field>
          </div>

          <div className={styles.grille2}>
            <Field label={t('regie:ventes.reglages.adresse')} htmlFor="pf-adresse">
              <Input
                id="pf-adresse"
                value={ligne1}
                onChange={(event) => setLigne1(event.target.value)}
              />
            </Field>
            <div className={styles.grille2}>
              <Field label={t('regie:ventes.reglages.codePostal')} htmlFor="pf-cp">
                <Input
                  id="pf-cp"
                  value={codePostal}
                  onChange={(event) => setCodePostal(event.target.value)}
                />
              </Field>
              <Field label={t('regie:ventes.reglages.ville')} htmlFor="pf-ville">
                <Input
                  id="pf-ville"
                  value={ville}
                  onChange={(event) => setVille(event.target.value)}
                />
              </Field>
            </div>
          </div>

          <div className={styles.grille2}>
            <Field label={t('regie:ventes.reglages.pays')} htmlFor="pf-pays">
              <Input
                id="pf-pays"
                value={pays}
                maxLength={2}
                onChange={(event) => setPays(event.target.value.toUpperCase())}
              />
            </Field>
            <Field label={t('regie:ventes.reglages.tvaIntracom')} htmlFor="pf-tva-intracom">
              <Input
                id="pf-tva-intracom"
                value={tvaIntracom}
                onChange={(event) => setTvaIntracom(event.target.value)}
              />
            </Field>
          </div>
        </div>

        <div className={styles.groupe}>
          <div className={styles.grille2}>
            <Field label={t('regie:ventes.reglages.iban')} htmlFor="pf-iban">
              <Input id="pf-iban" value={iban} onChange={(event) => setIban(event.target.value)} />
            </Field>
            <Field label={t('regie:ventes.reglages.bic')} htmlFor="pf-bic">
              <Input id="pf-bic" value={bic} onChange={(event) => setBic(event.target.value)} />
            </Field>
          </div>
        </div>

        <div className={styles.groupe}>
          <div className={styles.cases}>
            <Checkbox
              id="pf-assujetti"
              label={t('regie:ventes.reglages.assujetti')}
              checked={assujetti}
              onCheckedChange={(valeur) => setAssujetti(valeur === true)}
            />
            <Checkbox
              id="pf-franchise"
              label={t('regie:ventes.reglages.franchiseEnBase')}
              checked={franchiseEnBase}
              onCheckedChange={(valeur) => setFranchiseEnBase(valeur === true)}
            />
          </div>

          <div className={styles.grille2}>
            <Field label={t('regie:ventes.reglages.numeroTva')} htmlFor="pf-numero-tva">
              <Input
                id="pf-numero-tva"
                value={numeroTva}
                onChange={(event) => setNumeroTva(event.target.value)}
              />
            </Field>
            <Field label={t('regie:ventes.reglages.regimeParDefaut')} htmlFor="pf-regime">
              <Select
                id="pf-regime"
                aria-label={t('regie:ventes.reglages.regimeParDefaut')}
                value={regimeParDefaut}
                onValueChange={(valeur) => setRegimeParDefaut(valeur as RegimeTva)}
                options={REGIMES_TVA.map((code) => ({
                  value: code,
                  label: t(`ventes:regimes.${code}`),
                }))}
              />
            </Field>
          </div>
        </div>

        <div className={styles.groupe}>
          <div className={styles.grille2}>
            <Field label={t('regie:ventes.reglages.delaiPaiement')} htmlFor="pf-delai">
              <Input
                id="pf-delai"
                type="number"
                min={0}
                max={365}
                value={delaiPaiementJours}
                onChange={(event) => setDelaiPaiementJours(event.target.value)}
              />
            </Field>
            <Field
              label={t('regie:ventes.reglages.droitDeSuiteBeneficiaire')}
              htmlFor="pf-beneficiaire"
            >
              <Input
                id="pf-beneficiaire"
                value={droitDeSuiteBeneficiaire}
                onChange={(event) => setDroitDeSuiteBeneficiaire(event.target.value)}
              />
            </Field>
          </div>

          <Checkbox
            id="pf-droit-de-suite-acheteur"
            label={t('regie:ventes.reglages.droitDeSuiteAChargeAcheteur')}
            checked={droitDeSuiteAChargeAcheteur}
            onCheckedChange={(valeur) => setDroitDeSuiteAChargeAcheteur(valeur === true)}
          />

          <p className={styles.bareme}>
            {t('regie:ventes.reglages.baremeResume', {
              seuil: formatPrice(profil.droitDeSuite.seuilCentimes, 'EUR', langue),
              plafond: formatPrice(profil.droitDeSuite.plafondCentimes, 'EUR', langue),
              count: profil.droitDeSuite.tranches.length,
            })}
          </p>
        </div>

        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        {enregistre && !erreur ? (
          <p className={styles.confirmation}>{t('regie:ventes.reglages.enregistre')}</p>
        ) : null}

        <div className={styles.actions}>
          <Button onClick={() => void soumettre()} loading={modifier.isPending}>
            {t('commun:actions.enregistrer')}
          </Button>
        </div>
      </div>

      {/* L'en-tête tel qu'il s'imprimera, relu pendant la saisie. Il redit les
          champs du formulaire : un lecteur d'écran n'en a pas besoin. */}
      <div className={styles.apercu} aria-hidden="true">
        <div className={styles.apercuFeuille}>
          <div className={styles.apercuEntete}>
            <div className={styles.apercuEmetteur}>
              <strong>{raisonSociale || '—'}</strong>
              {ligne1 ? <span>{ligne1}</span> : null}
              {villeComplete ? <span>{villeComplete}</span> : null}
              {pays ? <span>{pays}</span> : null}
            </div>
            <span className={styles.apercuNature}>{t('ventes:factures.facture')}</span>
          </div>
          <dl className={styles.apercuIdentifiants}>
            {siren ? (
              <div>
                <dt>{t('regie:ventes.reglages.siren')}</dt>
                <dd>{siren}</dd>
              </div>
            ) : null}
            {tvaIntracom ? (
              <div>
                <dt>{t('regie:ventes.reglages.tvaIntracom')}</dt>
                <dd>{tvaIntracom}</dd>
              </div>
            ) : null}
            {iban ? (
              <div>
                <dt>{t('regie:ventes.reglages.iban')}</dt>
                <dd>{iban}</dd>
              </div>
            ) : null}
            {bic ? (
              <div>
                <dt>{t('regie:ventes.reglages.bic')}</dt>
                <dd>{bic}</dd>
              </div>
            ) : null}
          </dl>
          <span className={styles.apercuLignes} />
        </div>
      </div>
    </section>
  );
}
